import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Sparkles, CreditCard, IndianRupee } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { SUBSCRIPTION } from '@/constants/testIds';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
	return new Promise((resolve) => {
		if (window.Razorpay) return resolve(true);
		const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
		if (existing) {
			existing.addEventListener('load', () => resolve(true));
			existing.addEventListener('error', () => resolve(false));
			return;
		}
		const s = document.createElement('script');
		s.src = RAZORPAY_SCRIPT_SRC;
		s.async = true;
		s.onload = () => resolve(true);
		s.onerror = () => resolve(false);
		document.body.appendChild(s);
	});
}

function fmt(amount, currency) {
	if (currency === 'usd') return `$${Number(amount).toFixed(2)}`;
	return `₹${Math.round(Number(amount))}`;
}

export default function Subscriptions() {
	const { user, refresh } = useAuth();
	const [plans, setPlans] = useState([]);
	const [config, setConfig] = useState(null);
	const [busy, setBusy] = useState(false);
	const [pollingMsg, setPollingMsg] = useState(null);
	const [chooseOpen, setChooseOpen] = useState(false);
	const [billing, setBilling] = useState('yearly'); // monthly | yearly
	const [params, setParams] = useSearchParams();

	useEffect(() => {
		(async () => {
			try {
				const [{ data: p }, { data: c }] = await Promise.all([
					api.get('/subscription/plans'),
					api.get('/payments/config'),
				]);
				setPlans(p.plans || []);
				setConfig(c || null);
			} catch (err) {
				toast.error(formatApiError(err));
			}
		})();
	}, []);

	const pollStripe = useCallback(async (sessionId, attempts = 0) => {
		const max = 6;
		if (attempts >= max) {
			setPollingMsg(null);
			toast.error('We could not confirm your payment in time. Please refresh in a moment.');
			return;
		}
		try {
			const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
			if (data.payment_status === 'paid') {
				setPollingMsg(null);
				toast.success('Payment received — Welcome to Premium!');
				await refresh();
				return;
			}
			if (data.status === 'expired') {
				setPollingMsg(null);
				toast.error('Payment session expired.');
				return;
			}
			setPollingMsg('Confirming payment…');
			setTimeout(() => pollStripe(sessionId, attempts + 1), 2000);
		} catch (err) {
			setPollingMsg(null);
			toast.error(formatApiError(err));
		}
	}, [refresh]);

	useEffect(() => {
		const sessionId = params.get('session_id');
		const provider = params.get('provider');
		const cancelled = params.get('cancelled');
		if (sessionId && provider === 'stripe') {
			setPollingMsg('Confirming payment…');
			pollStripe(sessionId);
			const next = new URLSearchParams(params);
			next.delete('session_id');
			next.delete('provider');
			setParams(next, { replace: true });
		} else if (cancelled) {
			toast.info('Checkout cancelled.');
			const next = new URLSearchParams(params);
			next.delete('cancelled');
			setParams(next, { replace: true });
		}
	}, []); // eslint-disable-line

	const isPremium = user?.plan === 'PREMIUM' || user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
	const isAdminRole = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

	const pkgINR = billing === 'yearly' ? 'premium_yearly_inr' : 'premium_monthly_inr';
	const pkgUSD = billing === 'yearly' ? 'premium_yearly_usd' : 'premium_monthly_usd';
	const inrAmount = config?.pricing?.[billing === 'yearly' ? 'yearly_inr' : 'monthly_inr'];
	const usdAmount = config?.pricing?.[billing === 'yearly' ? 'yearly_usd' : 'monthly_usd'];
	const monthlyINR = config?.pricing?.monthly_inr;
	const yearlyINR = config?.pricing?.yearly_inr;
	const yearlySavingsPct = monthlyINR && yearlyINR ? Math.max(0, Math.round((1 - (yearlyINR / (monthlyINR * 12))) * 100)) : 0;

	const payWithStripe = async () => {
		setBusy(true);
		try {
			const { data } = await api.post('/payments/stripe/checkout', {
				package_id: pkgUSD,
				origin_url: window.location.origin,
			});
			window.location.href = data.url;
		} catch (err) {
			setBusy(false);
			toast.error(formatApiError(err, 'Could not start Stripe checkout'));
		}
	};

	const payWithRazorpay = async () => {
		setBusy(true);
		const loaded = await loadRazorpayScript();
		if (!loaded) {
			setBusy(false);
			toast.error('Could not load Razorpay. Check your network and try again.');
			return;
		}
		let order;
		try {
			const { data } = await api.post('/payments/razorpay/order', { package_id: pkgINR });
			order = data;
		} catch (err) {
			setBusy(false);
			toast.error(formatApiError(err, 'Razorpay is not configured yet'));
			return;
		}
		const rzp = new window.Razorpay({
			key: order.key_id,
			amount: order.amount,
			currency: order.currency,
			name: order.name,
			description: order.description,
			order_id: order.order_id,
			prefill: { name: user?.name || '', email: user?.email || '' },
			theme: { color: '#1E3A8A' },
			handler: async (resp) => {
				try {
					await api.post('/payments/razorpay/verify', {
						razorpay_order_id: resp.razorpay_order_id,
						razorpay_payment_id: resp.razorpay_payment_id,
						razorpay_signature: resp.razorpay_signature,
					});
					toast.success('Payment received — Welcome to Premium!');
					await refresh();
				} catch (err) {
					toast.error(formatApiError(err, 'Payment verification failed'));
				} finally {
					setBusy(false);
				}
			},
			modal: { ondismiss: () => setBusy(false) },
		});
		rzp.on('payment.failed', (resp) => {
			setBusy(false);
			toast.error(resp?.error?.description || 'Payment failed');
		});
		rzp.open();
	};

	const startUpgrade = () => setChooseOpen(true);

	const downgrade = async () => {
		if (!window.confirm('Downgrade to Free? Your encrypted vault items will be preserved but read-only.')) return;
		setBusy(true);
		try {
			await api.post('/subscription/downgrade');
			await refresh();
			toast.success('Plan updated');
		} catch (err) {
			toast.error(formatApiError(err));
		} finally {
			setBusy(false);
		}
	};

	const premium = plans.find((p) => p.key === 'PREMIUM');
	const free = plans.find((p) => p.key === 'FREE');

	return (
		<Layout>
			<div data-testid={SUBSCRIPTION.root} className="space-y-8">
				<div className="text-center max-w-2xl mx-auto">
					<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Plans</div>
					<h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">
						Choose how far you&apos;d like to go.
					</h1>
					<p className="mt-3 text-slate-600">
						Free is enough for most families. Premium unlocks the Family Resilience Coach™ + Private Vault.
					</p>
				</div>

				{/* Monthly / Yearly toggle */}
				<div className="flex justify-center">
					<div className="inline-flex p-1 bg-slate-100 rounded-full">
						<button
							type="button"
							onClick={() => setBilling('monthly')}
							data-testid="subscription-toggle-monthly"
							className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${billing === 'monthly' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
						>
							Monthly
						</button>
						<button
							type="button"
							onClick={() => setBilling('yearly')}
							data-testid="subscription-toggle-yearly"
							className={`px-5 py-2 rounded-full text-sm font-semibold transition-all inline-flex items-center gap-2 ${billing === 'yearly' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
						>
							Yearly
							{yearlySavingsPct > 0 && (
								<span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
									Save {yearlySavingsPct}%
								</span>
							)}
						</button>
					</div>
				</div>

				{pollingMsg && (
					<div className="max-w-md mx-auto rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-5 py-3 text-sm flex items-center gap-3">
						<span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-soft" />
						{pollingMsg}
					</div>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
					{free && (
						<div className="p-8 rounded-3xl border card-soft">
							<h3 className="font-heading text-2xl font-semibold text-slate-900">{free.title}</h3>
							<p className="text-slate-600 mt-1">{free.tagline}</p>
							<div className="mt-5 text-4xl font-heading font-bold text-slate-900">{free.price}</div>
							<ul className="mt-6 space-y-3">
								{free.features.map((f) => (
									<li key={f} className="flex items-start gap-3">
										<span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 mt-0.5"><Check className="w-3 h-3" /></span>
										<span className="text-slate-700 text-sm">{f}</span>
									</li>
								))}
							</ul>
							<div className="mt-8">
								<Button disabled variant="outline" className="rounded-full w-full">
									{isPremium ? 'Available anytime' : 'Your current plan'}
								</Button>
							</div>
						</div>
					)}
					{premium && (
						<div className="p-8 rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white shadow-lg">
							<div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-3">
								<Sparkles className="w-3.5 h-3.5" /> Most loved
							</div>
							<h3 className="font-heading text-2xl font-semibold text-slate-900">{premium.title}</h3>
							<p className="text-slate-600 mt-1">{premium.tagline}</p>

							<div className="mt-5 flex items-baseline gap-2 flex-wrap">
								<div className="text-4xl font-heading font-bold text-slate-900">{inrAmount != null ? fmt(inrAmount, 'inr') : '—'}</div>
								<div className="text-slate-500 text-sm">/ {billing === 'yearly' ? 'year' : 'month'}</div>
								{usdAmount != null && (
									<div className="text-slate-400 text-sm">· or {fmt(usdAmount, 'usd')} for international</div>
								)}
							</div>
							{billing === 'yearly' && monthlyINR && yearlyINR && (
								<div className="text-xs text-emerald-700 font-semibold mt-1">
									Effective ₹{Math.round(yearlyINR / 12)}/month — saves you ₹{Math.max(0, Math.round(monthlyINR * 12 - yearlyINR))} a year.
								</div>
							)}

							<ul className="mt-6 space-y-3">
								{premium.features.map((f) => (
									<li key={f} className="flex items-start gap-3">
										<span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 mt-0.5"><Check className="w-3 h-3" /></span>
										<span className="text-slate-700 text-sm">{f}</span>
									</li>
								))}
							</ul>

							<div className="mt-8">
								{isAdminRole ? (
									<Button disabled variant="outline" className="rounded-full w-full">
										Included with your admin role
									</Button>
								) : isPremium ? (
									<Button onClick={downgrade} disabled={busy} variant="outline" className="rounded-full w-full" data-testid={SUBSCRIPTION.downgradeButton}>
										{busy ? 'Updating…' : 'Switch back to Free'}
									</Button>
								) : (
									<Button onClick={startUpgrade} disabled={busy} className="rounded-full w-full bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={SUBSCRIPTION.upgradeButton}>
										{busy ? 'Working…' : `Upgrade — ${inrAmount != null ? fmt(inrAmount, 'inr') : ''} / ${billing === 'yearly' ? 'year' : 'month'}`}
									</Button>
								)}
							</div>
							{isPremium && <div className="mt-3 text-center text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">{isAdminRole ? 'Premium by admin role' : 'Your current plan'}</div>}
						</div>
					)}
				</div>

				<div className="max-w-3xl mx-auto card-soft p-6 text-sm text-slate-600 leading-relaxed">
					Privacy is our strongest promise. Premium data is encrypted on your device. Admins, Super Admins, developers and support engineers cannot read it. Only you can.
				</div>

				<Dialog open={chooseOpen} onOpenChange={setChooseOpen}>
					<DialogContent className="rounded-3xl max-w-md">
						<DialogHeader>
							<DialogTitle>How would you like to pay?</DialogTitle>
						</DialogHeader>
						<div className="space-y-3 mt-2">
							<button
								type="button"
								onClick={() => { setChooseOpen(false); payWithRazorpay(); }}
								disabled={busy || !config?.razorpay_enabled}
								data-testid="subscription-pay-razorpay-button"
								className="w-full text-left px-5 py-4 rounded-2xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-4"
							>
								<div className="w-11 h-11 rounded-2xl bg-blue-50 text-blue-800 grid place-items-center"><IndianRupee className="w-5 h-5" /></div>
								<div className="flex-1">
									<div className="font-heading font-semibold text-slate-900">Pay {inrAmount != null ? fmt(inrAmount, 'inr') : ''} with Razorpay</div>
									<div className="text-xs text-slate-500 mt-0.5">UPI · cards · wallets · netbanking</div>
								</div>
								{!config?.razorpay_enabled && (
									<span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-1 rounded-full">Coming soon</span>
								)}
							</button>
							<button
								type="button"
								onClick={() => { setChooseOpen(false); payWithStripe(); }}
								disabled={busy || !config?.stripe_enabled}
								data-testid="subscription-pay-stripe-button"
								className="w-full text-left px-5 py-4 rounded-2xl border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-4"
							>
								<div className="w-11 h-11 rounded-2xl bg-violet-50 text-violet-800 grid place-items-center"><CreditCard className="w-5 h-5" /></div>
								<div className="flex-1">
									<div className="font-heading font-semibold text-slate-900">Pay {usdAmount != null ? fmt(usdAmount, 'usd') : ''} with Stripe</div>
									<div className="text-xs text-slate-500 mt-0.5">International cards via Stripe Checkout</div>
								</div>
							</button>
						</div>
						<DialogFooter className="mt-4">
							<Button variant="outline" className="rounded-full" onClick={() => setChooseOpen(false)}>Cancel</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</Layout>
	);
}
